import { Fragment, useCallback } from 'react';
import {
  enqueueMailcomRenewal,
  listMailcomRenewals,
  refreshMailcomSession,
  type WireRenewalJob,
} from '../../api/endpoints.js';
import { useApiMutation, useApiQuery } from '../../api/hooks.js';
import {
  Badge,
  Banner,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  Modal,
  TBody,
  TD,
  TH,
  THead,
  TRow,
  Table,
} from '../../ui/index.js';
import { ELIGIBILITY_LABEL, RENEWAL_STATUS_LABEL, fmtDate, toneForRenewal } from './shared.js';

/**
 * A server code / error with soft line-break opportunities after "_", ".",
 * ":" and "/", so a narrow cell wraps "…has_leased_" / "alias" instead of at
 * an arbitrary letter. Text content is unchanged. One wrapper element, so a
 * stacked (flex) cell sees a single item.
 */
function SoftBreaks({ text }: { readonly text: string }): JSX.Element {
  const parts = text.split(/(?<=[_.:/])/);
  return (
    <span>
      {parts.map((p, i) => (
        <Fragment key={i}>
          {i > 0 ? <wbr /> : null}
          {p}
        </Fragment>
      ))}
    </span>
  );
}

export interface RenewalHistoryModalProps {
  readonly providerId: string;
  readonly open: boolean;
  readonly accountId: string | null;
  readonly canManage: boolean;
  readonly onClose: () => void;
}

export function RenewalHistoryModal({
  providerId,
  open,
  accountId,
  canManage,
  onClose,
}: RenewalHistoryModalProps): JSX.Element | null {
  if (!open || !accountId) return null;
  return (
    <Modal open={open} onClose={onClose} title="Yenileme ve Oturum Geçmişi" width={960} testId="renewal-history-modal">
      <RenewalBody providerId={providerId} accountId={accountId} canManage={canManage} />
    </Modal>
  );
}

function RenewalBody({ providerId, accountId, canManage }: { readonly providerId: string; readonly accountId: string; readonly canManage: boolean }): JSX.Element {
  const renewalsQuery = useApiQuery<{ jobs: readonly WireRenewalJob[] }>(
    (o) => listMailcomRenewals(o, accountId, providerId),
    [accountId, providerId],
  );
  const enqueueMutation = useApiMutation(
    (o, input: { accountId: string; aliasId: string | null }) =>
      enqueueMailcomRenewal(o, input.accountId, input.aliasId, providerId),
  );
  const sessionMutation = useApiMutation((o, input: string) => refreshMailcomSession(o, input, providerId));
  // 2026-09-22 — the "Hata" column shows only when the route reports lastError
  // at all (the generic route sends it, null included); the mail.com-literal
  // renewals route omits it, so that modal keeps its previous columns instead
  // of a misleading "—" on failed jobs.
  const hasLastError =
    renewalsQuery.kind === 'ok'
    && (renewalsQuery.data?.jobs ?? []).some((j) => j.lastError !== undefined);

  const handleEnqueue = useCallback(async () => {
    const result = await enqueueMutation.run({ accountId, aliasId: null });
    if (result.kind === 'ok') renewalsQuery.reload();
  }, [enqueueMutation, accountId, renewalsQuery]);

  const handleSessionRefresh = useCallback(async () => {
    const result = await sessionMutation.run(accountId);
    if (result.kind === 'ok') renewalsQuery.reload();
  }, [sessionMutation, accountId, renewalsQuery]);

  return (
    <div>
      <div
        className="mcp-modal-toolbar"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}
      >
        <span className="t-tertiary t-small mcp-modal-meta">
          Account ID: <code className="t-mono">{accountId.slice(0, 8)}…</code>
        </span>
        <div className="mcp-modal-actions" style={{ display: 'flex', gap: 8 }}>
          <Button variant="ghost" size="sm" onClick={() => renewalsQuery.reload()} icon={<Icon.Refresh size={12} />}>
            Yenile
          </Button>
          {canManage ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleSessionRefresh}
              loading={sessionMutation.state.kind === 'running'}
              icon={<Icon.Sparkle size={12} />}
            >
              Oturum yenile
            </Button>
          ) : null}
          {canManage ? (
            <Button
              variant="primary"
              size="sm"
              onClick={handleEnqueue}
              loading={enqueueMutation.state.kind === 'running'}
              icon={<Icon.Refresh size={12} />}
            >
              Yenileme kuyruğuna al
            </Button>
          ) : null}
        </div>
      </div>

      <p className="t-tertiary t-small" style={{ margin: '0 0 12px' }}>
        Oturum yenileme isteği <code>MailcomSessionRefreshRequested</code> outbox event'i olarak yazılır;
        gerçek yenileme worker tarafında Temporal workflow ile yürütülür. Buradaki iş kayıtları
        provider_renewal_jobs tablosuna karşılık gelir.
      </p>

      {enqueueMutation.state.kind === 'error' ? (
        <div style={{ marginBottom: 8 }}>
          <Banner tone="danger">Yenileme isteği başarısız: {enqueueMutation.state.reason}</Banner>
        </div>
      ) : null}
      {enqueueMutation.state.kind === 'ok' ? (
        <div style={{ marginBottom: 8 }}>
          <Banner tone="success">Yenileme işi kuyruğa alındı.</Banner>
        </div>
      ) : null}
      {sessionMutation.state.kind === 'error' ? (
        <div style={{ marginBottom: 8 }}>
          <Banner tone="danger">Oturum yenileme başarısız: {sessionMutation.state.reason}</Banner>
        </div>
      ) : null}
      {sessionMutation.state.kind === 'ok' ? (
        <div style={{ marginBottom: 8 }}>
          <Banner tone="success">Oturum yenileme isteği kuyruğa alındı.</Banner>
        </div>
      ) : null}

      {renewalsQuery.kind === 'loading' ? <LoadingState /> : null}
      {renewalsQuery.kind === 'error' ? (
        <ErrorState reason={renewalsQuery.reason} onRetry={renewalsQuery.reload} />
      ) : null}
      {renewalsQuery.kind === 'ok' ? (
        (renewalsQuery.data?.jobs ?? []).length === 0 ? (
          <EmptyState label="Bu parent için yenileme işi yok." />
        ) : (
          <Table dense stack scroll className="mcp-renewal-table mcp-grid2 tbl-stack-lg">
            <THead>
              <TRow>
                <TH>Tür</TH>
                <TH>Durum</TH>
                <TH>Uygunluk</TH>
                <TH>Deneme</TH>
                <TH>Kuyruğa</TH>
                <TH>Tamamlandı</TH>
                {hasLastError ? <TH>Hata</TH> : null}
              </TRow>
            </THead>
            <TBody>
              {(renewalsQuery.data?.jobs ?? []).map((j) => (
                <TRow key={j.id}>
                  <TD className="t-mono t-xs tbl-stack-primary mcp-rh-type" label="Tür">{j.jobType}</TD>
                  <TD className="tbl-stack-status" label="Durum">
                    <Badge tone={toneForRenewal(j.status)} dot>
                      {RENEWAL_STATUS_LABEL[j.status] ?? j.status}
                    </Badge>
                  </TD>
                  <TD className="cell-wrap mcp-rh-elig" label="Uygunluk">
                    <Badge tone={j.eligibility === 'eligible' ? 'success' : 'warn'}>
                      {ELIGIBILITY_LABEL[j.eligibility] ?? j.eligibility}
                    </Badge>
                    {j.ineligibilityReason ? (
                      <span className="t-tertiary t-xs mcp-rh-reason" style={{ marginLeft: 6 }}>
                        (<SoftBreaks text={j.ineligibilityReason} />)
                      </span>
                    ) : null}
                  </TD>
                  <TD className="t-mono t-num" label="Deneme">{j.attempts}</TD>
                  <TD className="t-mono t-xs t-tertiary" label="Kuyruğa">{fmtDate(j.enqueuedAt)}</TD>
                  <TD className="t-mono t-xs t-tertiary" label="Tamamlandı">{fmtDate(j.completedAt)}</TD>
                  {/* 2026-09-22 — worker's last_error (server-truncated to 160
                      chars), e.g. gmx.web_login.verification_required. */}
                  {!hasLastError ? null : j.lastError ? (
                    <TD
                      className="t-mono t-xs cell-wrap tbl-stack-full mcp-rh-error"
                      label="Hata"
                      title={j.lastError}
                      style={{ color: 'var(--danger-600)', maxWidth: 240, wordBreak: 'break-word' }}
                      data-testid={`renewal-last-error-${j.id}`}
                    >
                      <SoftBreaks text={j.lastError} />
                    </TD>
                  ) : (
                    <TD className="t-xs t-tertiary" label="Hata" data-testid={`renewal-last-error-${j.id}`}>—</TD>
                  )}
                </TRow>
              ))}
            </TBody>
          </Table>
        )
      ) : null}
    </div>
  );
}
